export type Locale = "ko" | "en";

export function resolveLocale(input?: string): Locale {
  if (input === "en") return "en";
  return "ko";
}

/* ── Navigation labels ─────────────────────────────────────────────── */

export type Major = "dashboard" | "monitoring" | "operations" | "assets" | "infrastructure" | "auditLog";
export type Sub = "overview" | "fleet" | "live" | "equipment" | "alerts" | "workorders" | "gateways" | "gw" | "tree" | "policy" | "mode" | "auditViewer";

export const navLabels = {
  ko: {
    major: {
      dashboard: "대시보드",
      monitoring: "모니터링",
      operations: "운영관리",
      assets: "자산관리",
      infrastructure: "인프라 마스터",
      auditLog: "감사 로그",
    } as Record<Major, string>,
    sub: {
      overview: "종합 현황",
      fleet: "공장 플릿",
      live: "실시간 데이터",
      equipment: "설비 상태",
      alerts: "알람 센터",
      workorders: "작업지시",
      gateways: "게이트웨이",
      gw: "GW/수집 관리",
      tree: "자산 트리 설정",
      policy: "운영 정책",
      mode: "시스템 모드",
      auditViewer: "감사 뷰어",
    } as Record<Sub, string>,
  },
  en: {
    major: {
      dashboard: "Dashboard",
      monitoring: "Monitoring",
      operations: "Operations",
      assets: "Assets",
      infrastructure: "Infrastructure",
      auditLog: "Audit Log",
    } as Record<Major, string>,
    sub: {
      overview: "Overview",
      fleet: "Fleet",
      live: "Live Feed",
      equipment: "Equipment",
      alerts: "Alerts",
      workorders: "Work Orders",
      gateways: "Gateways",
      gw: "GW Management",
      tree: "Asset Tree",
      policy: "Ops Policy",
      mode: "System Mode",
      auditViewer: "Audit Viewer",
    } as Record<Sub, string>,
  },
} as const;

/* ── App bar & shell labels ────────────────────────────────────────── */

export const shellLabels = {
  ko: {
    appTitle: "FEMS",
    notifications: "알림",
    viewAll: "전체 보기",
    noAlerts: "미처리 알림 없음",
    shiftMode: "교대",
    pinSidebar: "사이드바 고정",
    unpinSidebar: "사이드바 숨기기",
    openMenu: "메뉴 열기",
    settings: "설정",
    devTools: "개발 도구",
  },
  en: {
    appTitle: "FEMS",
    notifications: "Notifications",
    viewAll: "View All",
    noAlerts: "No open alerts",
    shiftMode: "Shift",
    pinSidebar: "Pin sidebar",
    unpinSidebar: "Unpin sidebar",
    openMenu: "Open menu",
    settings: "Settings",
    devTools: "Dev Tools",
  },
} as const;

/* ── KPI labels per page ───────────────────────────────────────────── */

export const kpiLabels = {
  ko: {
    energy: "에너지", carbon: "탄소", cost: "비용", openAlerts: "미처리 알람",
    today: "오늘 누적", capUsage: "한도 대비",
    onlineSensors: "온라인 센서", dataQuality: "데이터 품질", feedRate: "수집 속도",
    staleSensors: "미갱신 센서", avgHealth: "평균 건전성", inspectNow: "즉시 점검",
    planPm: "예방정비", normal: "정상",
    critical: "치명적", high: "높음", openCount: "미처리", ackedCount: "확인됨",
    todo: "대기", inProgress: "진행중", done: "완료", overdue: "기한초과",
    totalEquipment: "전체 설비", totalSensors: "전체 센서", offline: "오프라인",
    onlineGw: "온라인 GW", totalGw: "전체 GW", degraded: "불안정", protocols: "프로토콜",
    health: "건전성", site: "공장", cap: "한도", gateways: "게이트웨이",
    protocol: "프로토콜", status: "상태",
  },
  en: {
    energy: "Energy", carbon: "Carbon", cost: "Cost", openAlerts: "Open Alerts",
    today: "Today", capUsage: "vs Cap",
    onlineSensors: "Online Sensors", dataQuality: "Data Quality", feedRate: "Feed Rate",
    staleSensors: "Stale Sensors", avgHealth: "Avg Health", inspectNow: "Inspect Now",
    planPm: "Plan PM", normal: "Normal",
    critical: "Critical", high: "High", openCount: "Open", ackedCount: "Acked",
    todo: "TODO", inProgress: "In Progress", done: "Done", overdue: "Overdue",
    totalEquipment: "Equipment", totalSensors: "Sensors", offline: "Offline",
    onlineGw: "Online GW", totalGw: "Total GW", degraded: "Degraded", protocols: "Protocols",
    health: "Health", site: "Site", cap: "Cap", gateways: "Gateways",
    protocol: "Protocol", status: "Status",
  },
} as const;

/* ── Legacy dictionary (kept for compatibility) ────────────────────── */

type Dict = {
  noData: string;
  badgeReady: string;
  title: string;
  siteInfo: (name: string, location: string) => string;
  generateReport: string;
  drPlan: string;
  weeklyEnergy: string;
  energyCost: string;
  peakDemand: string;
  avgPowerFactor: string;
  operationsBoard: string;
  operationsDesc: string;
  meters: string;
  targets: string;
  meter: string;
  type: string;
  kwh7d: string;
  peakKw: string;
  lastSeen: string;
  critical: string;
  weeklyCapUsage: string;
  roadmap: string;
  alertCenter: string;
  alertDesc: string;
  unitKwh: string;
  unitKw: string;
};

export const dictionaries: Record<Locale, Dict> = {
  ko: {
    noData: "데이터가 없습니다. DB migrate + seed를 먼저 실행해 주세요.",
    badgeReady: "2026 준비",
    title: "FEMS 관제 센터",
    siteInfo: (name, location) => `${name} · ${location}`,
    generateReport: "리포트 생성",
    drPlan: "수요반응 계획",
    weeklyEnergy: "주간 에너지",
    energyCost: "에너지 비용",
    peakDemand: "최대 수요전력",
    avgPowerFactor: "평균 역률",
    operationsBoard: "운영 보드",
    operationsDesc: "운영자 중심 컴팩트 모니터링 레이아웃",
    meters: "계측기",
    targets: "목표",
    meter: "미터",
    type: "유형",
    kwh7d: "kWh(7일)",
    peakKw: "최대 kW",
    lastSeen: "최근 수집",
    critical: "핵심",
    weeklyCapUsage: "주간 한도 사용률",
    roadmap: "AI 이상 탐지, DR 참여 점수, 요금 최적화, 탄소 회계 훅이 로드맵 베이스라인에 포함되어 있습니다.",
    alertCenter: "알림 센터",
    alertDesc: "플랜트 시스템의 Open / Acked 이슈",
    unitKwh: "kWh",
    unitKw: "kW",
  },
  en: {
    noData: "No data. Run db migrate + seed first.",
    badgeReady: "2026 Ready",
    title: "FEMS Control Center",
    siteInfo: (name, location) => `${name} · ${location}`,
    generateReport: "Generate Report",
    drPlan: "Demand Response Plan",
    weeklyEnergy: "Weekly Energy",
    energyCost: "Energy Cost",
    peakDemand: "Peak Demand",
    avgPowerFactor: "Avg Power Factor",
    operationsBoard: "Operations Board",
    operationsDesc: "Compact, operator-first monitoring layout",
    meters: "Meters",
    targets: "Targets",
    meter: "Meter",
    type: "Type",
    kwh7d: "kWh(7d)",
    peakKw: "Peak kW",
    lastSeen: "Last Seen",
    critical: "Critical",
    weeklyCapUsage: "Weekly cap usage",
    roadmap: "AI anomaly model, DR participation scoring, tariff optimizer, and carbon accounting hooks are included in roadmap baseline.",
    alertCenter: "Alert Center",
    alertDesc: "Open / Acked issues from plant systems",
    unitKwh: "kWh",
    unitKw: "kW",
  },
};

/* ── Translation helpers ───────────────────────────────────────────── */

export function translateMeterType(type: string, locale: Locale): string {
  const map = {
    ko: { MAIN: "메인", HVAC: "공조", PRODUCTION_LINE: "생산라인", COMPRESSOR: "컴프레서", LIGHTING: "조명", ETC: "기타" },
    en: { MAIN: "Main", HVAC: "HVAC", PRODUCTION_LINE: "Production Line", COMPRESSOR: "Compressor", LIGHTING: "Lighting", ETC: "Etc" },
  } as const;
  return map[locale][type as keyof (typeof map)[typeof locale]] ?? type;
}

export function localizeSiteName(code: string, name: string, locale: Locale): string {
  if (locale !== "ko") return name;
  const ko: Record<string, string> = {
    "KR-ICN-01": "인천 전자 공장",
    "KR-BSN-02": "부산 자동차부품 공장",
    "KR-DJG-03": "대전 식품 가공 공장",
    "KR-GMJ-04": "김제 화학 공장",
    "KR-PTK-05": "평택 배터리 공장",
  };
  return ko[code] ?? name;
}

export function localizeLineName(name: string, locale: Locale): string {
  if (locale !== "ko") return name;
  const ko: Record<string, string> = {
    "Main Production": "주 생산 라인",
    Utility: "유틸리티 라인",
  };
  return ko[name] ?? name;
}

const siteShortAliasKo: Record<string, string> = {
  "KR-ICN-01": "인천",
  "KR-BSN-02": "부산",
  "KR-DJG-03": "대전",
  "KR-GMJ-04": "김제",
  "KR-PTK-05": "평택",
};

const lineShortAliasKo: Record<string, string> = {
  "Main Production": "주생산",
  "주 생산 라인": "주생산",
  Utility: "유틸",
  "유틸리티 라인": "유틸",
};

const equipmentTokenKo: Record<string, string> = {
  MAIN_PANEL: "메인반",
  PRODUCTION_LINE: "생산",
  LIGHTING_PANEL: "조명반",
  HVAC_AHU: "공조기",
  COMPRESSOR: "콤프",
  CHILLER: "칠러",
  BOILER: "보일러",
  OVEN: "오븐",
  ETC: "설비",
};

const metricTokenKo: Record<string, string> = {
  DEMAND_KW: "수요전력",
  ENERGY_KWH: "전력량",
  POWER_FACTOR: "역률",
  VOLTAGE_V: "전압",
  CURRENT_A: "전류",
  GAS_M3: "가스",
  STEAM_TON: "스팀",
  TEMPERATURE_C: "온도",
  HUMIDITY_PCT: "습도",
};

function compactAsciiWhitespace(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

export function shortSiteAlias(siteCode: string, locale: Locale): string {
  if (locale !== "ko") return siteCode;
  return siteShortAliasKo[siteCode] ?? siteCode;
}

export function shortLineAlias(lineName: string, locale: Locale): string {
  if (locale !== "ko") return lineName;
  return lineShortAliasKo[lineName] ?? localizeLineName(lineName, locale);
}

export function shortEquipmentAlias(equipmentName: string, locale: Locale): string {
  if (locale !== "ko") return equipmentName;
  const normalized = compactAsciiWhitespace(equipmentName);
  const m = normalized.match(/^([A-Z_]+)(?:-([A-Z0-9]+))?$/);
  if (!m) return normalized;
  const base = equipmentTokenKo[m[1]] ?? m[1].replaceAll("_", " ");
  const suffix = m[2] ? ` ${m[2]}` : "";
  return `${base}${suffix}`;
}

export function shortSensorAlias(sensorName: string, locale: Locale): string {
  if (locale !== "ko") return sensorName;
  const normalized = compactAsciiWhitespace(sensorName);
  const metricKey = Object.keys(metricTokenKo).find((k) => normalized.endsWith(k));

  if (!metricKey) {
    return shortEquipmentAlias(normalized, locale);
  }

  const prefix = compactAsciiWhitespace(normalized.slice(0, -metricKey.length).replace(/[-_ ]+$/, ""));
  const metric = metricTokenKo[metricKey];
  if (!prefix) return metric;
  return `${shortEquipmentAlias(prefix, locale)} ${metric}`;
}

export function translateSeverity(severity: string, locale: Locale): string {
  const map = {
    ko: { LOW: "낮음", MEDIUM: "보통", HIGH: "높음", CRITICAL: "치명" },
    en: { LOW: "LOW", MEDIUM: "MEDIUM", HIGH: "HIGH", CRITICAL: "CRITICAL" },
  } as const;
  return map[locale][severity as keyof (typeof map)[typeof locale]] ?? severity;
}

export function severityColor(severity: string): string {
  const map: Record<string, string> = {
    CRITICAL: "bg-red-700 text-white",
    HIGH: "bg-orange-600 text-white",
    MEDIUM: "bg-amber-500/80 text-slate-900",
    LOW: "bg-slate-500 text-white",
  };
  return map[severity] ?? "bg-slate-400 text-white";
}

export function severityDotColor(severity: string): string {
  const map: Record<string, string> = {
    CRITICAL: "bg-red-500",
    HIGH: "bg-orange-500",
    MEDIUM: "bg-amber-500",
    LOW: "bg-slate-400",
  };
  return map[severity] ?? "bg-slate-400";
}

export function translateAlertStatus(status: string, locale: Locale): string {
  const map = {
    ko: { OPEN: "미처리", ACKED: "확인됨", RESOLVED: "해결됨" },
    en: { OPEN: "OPEN", ACKED: "ACKED", RESOLVED: "RESOLVED" },
  } as const;
  return map[locale][status as keyof (typeof map)[typeof locale]] ?? status;
}

function parseThresholdTitle(title: string): { equipmentType: string; metric: string } | null {
  const normalized = compactAsciiWhitespace(title);
  const m = normalized.match(/^([A-Z_]+)\s+([A-Z_]+)\s+threshold breach$/i);
  if (!m) return null;
  return { equipmentType: m[1].toUpperCase(), metric: m[2].toUpperCase() };
}

function parseThresholdMessage(message: string): {
  lineName: string;
  equipmentName: string;
  metric: string | null;
  value: string;
  threshold: string;
} | null {
  const normalized = compactAsciiWhitespace(message);
  const m = normalized.match(/^(.+?)\/([A-Z_]+-[A-Z0-9]+)\s+([A-Z_]+-[A-Z_]+)\s+value=([0-9.]+)\s+threshold=([0-9.]+)$/);
  if (!m) return null;
  const sensorToken = m[3];
  const metric = sensorToken.includes("-") ? sensorToken.split("-").slice(1).join("-") : null;
  return {
    lineName: m[1],
    equipmentName: m[2],
    metric: metric ? metric.toUpperCase() : null,
    value: m[4],
    threshold: m[5],
  };
}

export function localizeAlertContent(
  title: string,
  message: string,
  locale: Locale
): { title: string; message: string } {
  if (locale !== "ko") return { title, message };

  const fallbackTitleKo: Record<string, string> = {
    "Peak demand anomaly": "피크 수요 이상 징후",
  };
  const fallbackMessageKo: Record<string, string> = {
    "Demand exceeded dynamic threshold for 15 minutes.": "수요전력이 15분 동안 동적 임계치를 초과했습니다.",
  };

  const parsedTitle = parseThresholdTitle(title);
  const parsedMessage = parseThresholdMessage(message);

  const titleText = parsedTitle
    ? `${translateEquipmentType(parsedTitle.equipmentType, locale)} ${translateMetricType(parsedTitle.metric, locale)} 임계 초과`
    : (fallbackTitleKo[title] ?? title);

  if (!parsedMessage) {
    return { title: titleText, message: fallbackMessageKo[message] ?? message };
  }

  const metricText = parsedMessage.metric ? translateMetricType(parsedMessage.metric, locale) : "측정값";
  const lineText = shortLineAlias(parsedMessage.lineName, locale);
  const equipmentText = shortEquipmentAlias(parsedMessage.equipmentName, locale);
  const messageText = `${lineText}/${equipmentText} ${metricText} ${parsedMessage.value} (임계 ${parsedMessage.threshold})`;
  return { title: titleText, message: messageText };
}

export function alertStatusColor(status: string): string {
  const map: Record<string, string> = {
    OPEN: "bg-red-600 text-white",
    ACKED: "bg-amber-500 text-white",
    RESOLVED: "bg-emerald-600 text-white",
  };
  return map[status] ?? "bg-slate-400 text-white";
}

export function translateWorkOrderStatus(status: string, locale: Locale): string {
  const map = {
    ko: { TODO: "대기", IN_PROGRESS: "진행중", DONE: "완료" },
    en: { TODO: "TODO", IN_PROGRESS: "IN PROGRESS", DONE: "DONE" },
  } as const;
  return map[locale][status as keyof (typeof map)[typeof locale]] ?? status;
}

export function workOrderStatusColor(status: string): string {
  const map: Record<string, string> = {
    TODO: "bg-slate-500 text-white",
    IN_PROGRESS: "bg-blue-600 text-white",
    DONE: "bg-emerald-600 text-white",
  };
  return map[status] ?? "bg-slate-400 text-white";
}

export function translateEquipmentType(type: string, locale: Locale): string {
  const map = {
    ko: {
      MAIN_PANEL: "주배전반", CHILLER: "칠러", BOILER: "보일러", COMPRESSOR: "컴프레서",
      PRODUCTION_LINE: "생산라인", OVEN: "오븐", HVAC_AHU: "공조기", LIGHTING_PANEL: "조명반", ETC: "기타",
    },
    en: {
      MAIN_PANEL: "Main Panel", CHILLER: "Chiller", BOILER: "Boiler", COMPRESSOR: "Compressor",
      PRODUCTION_LINE: "Production Line", OVEN: "Oven", HVAC_AHU: "HVAC/AHU", LIGHTING_PANEL: "Lighting Panel", ETC: "ETC",
    },
  } as const;
  return map[locale][type as keyof (typeof map)[typeof locale]] ?? type;
}

export function translateMetricType(metric: string, locale: Locale): string {
  const map = {
    ko: {
      ENERGY_KWH: "전력량(kWh)", DEMAND_KW: "수요전력(kW)", POWER_FACTOR: "역률",
      VOLTAGE_V: "전압(V)", CURRENT_A: "전류(A)", GAS_M3: "가스(m³)",
      STEAM_TON: "스팀(톤)", TEMPERATURE_C: "온도(°C)", HUMIDITY_PCT: "습도(%)",
    },
    en: {
      ENERGY_KWH: "Energy (kWh)", DEMAND_KW: "Demand (kW)", POWER_FACTOR: "Power Factor",
      VOLTAGE_V: "Voltage (V)", CURRENT_A: "Current (A)", GAS_M3: "Gas (m³)",
      STEAM_TON: "Steam (ton)", TEMPERATURE_C: "Temperature (°C)", HUMIDITY_PCT: "Humidity (%)",
    },
  } as const;
  return map[locale][metric as keyof (typeof map)[typeof locale]] ?? metric;
}

export function formatKoreanTimestamp(date: Date | string | null | undefined): string {
  if (!date) return "-";
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleString("ko-KR", {
    timeZone: "Asia/Seoul", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}

export function formatRelativeTime(date: Date | string | null | undefined, locale: Locale): string {
  if (!date) return "-";
  const d = typeof date === "string" ? new Date(date) : date;
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHour = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (locale === "ko") {
    if (diffMin < 1) return "방금 전";
    if (diffMin < 60) return `${diffMin}분 전`;
    if (diffHour < 24) return `${diffHour}시간 전`;
    return `${diffDay}일 전`;
  }
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  return `${diffDay}d ago`;
}

/* ── Auth labels ──────────────────────────────────────────────────── */

export const authLabels = {
  ko: {
    loginTitle: "FEMS 로그인",
    email: "이메일",
    password: "비밀번호",
    login: "로그인",
    loginError: "이메일 또는 비밀번호가 올바르지 않습니다",
    logout: "로그아웃",
    welcome: "환영합니다",
  },
  en: {
    loginTitle: "FEMS Login",
    email: "Email",
    password: "Password",
    login: "Sign In",
    loginError: "Invalid email or password",
    logout: "Sign Out",
    welcome: "Welcome",
  },
} as const;

/* ── Infrastructure labels ────────────────────────────────────────── */

export const infraLabels = {
  ko: {
    gwManagement: "게이트웨이/수집 관리",
    assetTree: "자산 트리 설정",
    opsPolicy: "운영 정책 설정",
    systemMode: "시스템 모드 설정",
    addGateway: "게이트웨이 추가",
    editGateway: "게이트웨이 편집",
    deleteGateway: "게이트웨이 삭제",
    tagMappings: "태그 매핑",
    addMapping: "매핑 추가",
    rawTag: "원시 태그",
    mappedSensor: "매핑된 센서",
    interpolation: "보간 방법",
    alarmThreshold: "알람 임계치",
    warnValue: "경고값",
    criticalValue: "위험값",
    direction: "방향",
    enabled: "활성",
    currentMode: "현재 모드",
    switchMode: "모드 전환",
    confirmModeChange: "모드를 전환하시겠습니까?",
    save: "저장",
    cancel: "취소",
    delete: "삭제",
    confirm: "확인",
    noSelection: "항목을 선택하세요",
    addArea: "구역 추가",
    addEquipment: "설비 추가",
    addSensor: "센서 추가",
  },
  en: {
    gwManagement: "Gateway / Ingestion Management",
    assetTree: "Asset Tree Configuration",
    opsPolicy: "Operations Policy Settings",
    systemMode: "System Mode Configuration",
    addGateway: "Add Gateway",
    editGateway: "Edit Gateway",
    deleteGateway: "Delete Gateway",
    tagMappings: "Tag Mappings",
    addMapping: "Add Mapping",
    rawTag: "Raw Tag",
    mappedSensor: "Mapped Sensor",
    interpolation: "Interpolation",
    alarmThreshold: "Alarm Threshold",
    warnValue: "Warning",
    criticalValue: "Critical",
    direction: "Direction",
    enabled: "Enabled",
    currentMode: "Current Mode",
    switchMode: "Switch Mode",
    confirmModeChange: "Switch system mode?",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    confirm: "Confirm",
    noSelection: "Select an item",
    addArea: "Add Area",
    addEquipment: "Add Equipment",
    addSensor: "Add Sensor",
  },
} as const;

/* ── Audit labels ─────────────────────────────────────────────────── */

export const auditLabels = {
  ko: {
    title: "감사 로그",
    timestamp: "시각",
    user: "사용자",
    action: "액션",
    target: "대상",
    detail: "상세",
    todayRecords: "오늘 기록",
    activeUsers: "활동 사용자",
    topAction: "최다 액션",
    lastRecord: "최근 기록",
    filterUser: "사용자 필터",
    filterAction: "액션 필터",
    all: "전체",
  },
  en: {
    title: "Audit Log",
    timestamp: "Timestamp",
    user: "User",
    action: "Action",
    target: "Target",
    detail: "Detail",
    todayRecords: "Today",
    activeUsers: "Active Users",
    topAction: "Top Action",
    lastRecord: "Last Record",
    filterUser: "Filter User",
    filterAction: "Filter Action",
    all: "All",
  },
} as const;

/* ── Translation helpers for new enums ────────────────────────────── */

export function translateUserRole(role: string, locale: Locale): string {
  const map = {
    ko: { ADMIN: "관리자", SUPERVISOR: "감독자", TEAM_LEAD: "조장", OPERATOR: "운영자" },
    en: { ADMIN: "Admin", SUPERVISOR: "Supervisor", TEAM_LEAD: "Team Lead", OPERATOR: "Operator" },
  } as const;
  return map[locale][role as keyof (typeof map)[typeof locale]] ?? role;
}

export function translateAuditAction(action: string, locale: Locale): string {
  const map = {
    ko: {
      CREATE: "생성", UPDATE: "수정", DELETE: "삭제",
      LOGIN: "로그인", LOGOUT: "로그아웃",
      MODE_CHANGE: "모드 변경", ACK: "확인", RESOLVE: "해결",
    },
    en: {
      CREATE: "Create", UPDATE: "Update", DELETE: "Delete",
      LOGIN: "Login", LOGOUT: "Logout",
      MODE_CHANGE: "Mode Change", ACK: "Acknowledge", RESOLVE: "Resolve",
    },
  } as const;
  return map[locale][action as keyof (typeof map)[typeof locale]] ?? action;
}

export function translateInterpolationMethod(method: string, locale: Locale): string {
  const map = {
    ko: { LINEAR: "선형", SPLINE: "스플라인", LAST_VALUE: "최종값", NONE: "없음" },
    en: { LINEAR: "Linear", SPLINE: "Spline", LAST_VALUE: "Last Value", NONE: "None" },
  } as const;
  return map[locale][method as keyof (typeof map)[typeof locale]] ?? method;
}

export function auditActionColor(action: string): string {
  const map: Record<string, string> = {
    CREATE: "bg-emerald-600 text-white",
    UPDATE: "bg-blue-600 text-white",
    DELETE: "bg-red-600 text-white",
    LOGIN: "bg-indigo-600 text-white",
    LOGOUT: "bg-slate-500 text-white",
    MODE_CHANGE: "bg-amber-600 text-white",
    ACK: "bg-cyan-600 text-white",
    RESOLVE: "bg-emerald-600 text-white",
  };
  return map[action] ?? "bg-slate-400 text-white";
}
